// frontend/src/app/services/settings.service.ts
import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap, catchError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LogService } from './log.service';

export interface NotificationPreferences {
  email: {
    enabled: boolean;
    stockAlerts: boolean;
    recommendations: boolean;
    events: boolean;
    promotions: boolean;
  };
  push: {
    enabled: boolean;
    urgentAlerts: boolean;
    dailyReminders: boolean;
  };
  weeklyReport: {
    enabled: boolean;
    dayOfWeek: number;
    hourOfDay: number;
  };
}

export interface AlgorithmWeights {
  weather: number;
  events: number;
  history: number;
}

export interface DashboardPreferences {
  defaultView: 'day' | 'week' | 'month';
  showWeather: boolean;
  showEvents: boolean;
  showTips: boolean;
  pinnedWidgets: string[];
}

export interface UserSettings {
  // Format plat (pour compatibilité avec le composant existant)
  emailNotifications: boolean;
  pushNotifications: boolean;
  weeklyReport: boolean;
  conservatismLevel: 'low' | 'medium' | 'high';
  autoAdjust: boolean;
  defaultExportFormat: 'pdf' | 'excel' | 'csv';
  includeGraphs: boolean;
  language: string;
  theme: 'light' | 'dark' | 'auto';
  twoFactorEnabled: boolean;
  lastPasswordChange?: Date;

  // Format détaillé
  notificationPreferences?: NotificationPreferences;
  algorithmWeights?: AlgorithmWeights;
  dashboardPreferences?: DashboardPreferences;
  dashboardLayout?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

/** Informations affichées avant la suppression du compte (BUG-04) */
export interface AccountDeletionPreview {
  paid: boolean;
  plan: string | null;
  periodEnd: string | null;
  twoFactorRequired: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private readonly apiUrl = `${environment.apiUrl}/settings`;
  private log = inject(LogService);

  // Signal state
  private _settings = signal<UserSettings | null>(null);
  private _loading = signal(false);

  // Public readonly signals
  readonly settings = this._settings.asReadonly();
  readonly loading = this._loading.asReadonly();

  constructor(private http: HttpClient) {}

  /**
   * Récupère les paramètres utilisateur
   */
  getSettings(): Observable<ApiResponse<UserSettings>> {
    if (environment.mockApi) {
      return of(this.getMockSettings());
    }

    this._loading.set(true);
    return this.http.get<ApiResponse<UserSettings>>(this.apiUrl).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this._settings.set(response.data);
        }
        this._loading.set(false);
      }),
      catchError((error) => {
        this.log.error('Error fetching settings', { error: error.message });
        this._loading.set(false);
        return of({ success: false, error: error.message });
      }),
    );
  }

  /**
   * Met à jour les paramètres
   */
  updateSettings(
    settings: Partial<UserSettings>,
  ): Observable<ApiResponse<UserSettings>> {
    if (environment.mockApi) {
      return of(this.mockUpdateSettings(settings));
    }

    this._loading.set(true);
    return this.http.put<ApiResponse<UserSettings>>(this.apiUrl, settings).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this._settings.set(response.data);
        }
        this._loading.set(false);
      }),
      catchError((error) => {
        this.log.error('Error updating settings', { error: error.message });
        this._loading.set(false);
        return of({ success: false, error: error.message });
      }),
    );
  }

  /**
   * Met à jour les préférences de notification
   */
  updateNotifications(
    preferences: Partial<NotificationPreferences>,
  ): Observable<ApiResponse<NotificationPreferences>> {
    if (environment.mockApi) {
      return of({ success: true, message: 'Notifications mises à jour' });
    }

    return this.http.put<ApiResponse<NotificationPreferences>>(
      `${this.apiUrl}/notifications`,
      preferences,
    );
  }

  /**
   * S'abonne aux notifications push
   */
  subscribeToPush(
    subscription: PushSubscription,
  ): Observable<ApiResponse<void>> {
    const body = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')),
        auth: this.arrayBufferToBase64(subscription.getKey('auth')),
      },
    };

    if (environment.mockApi) {
      return of({ success: true, message: 'Push activé' });
    }

    return this.http.post<ApiResponse<void>>(
      `${this.apiUrl}/notifications/push/subscribe`,
      body,
    );
  }

  /**
   * Se désabonne des notifications push
   */
  unsubscribeFromPush(): Observable<ApiResponse<void>> {
    if (environment.mockApi) {
      return of({ success: true, message: 'Push désactivé' });
    }

    return this.http.post<ApiResponse<void>>(
      `${this.apiUrl}/notifications/push/unsubscribe`,
      {},
    );
  }

  /**
   * Envoie un email de test
   */
  sendTestEmail(): Observable<ApiResponse<void>> {
    if (environment.mockApi) {
      return of({ success: true, message: 'Email de test envoyé' });
    }

    return this.http.post<ApiResponse<void>>(
      `${this.apiUrl}/notifications/test-email`,
      {},
    );
  }

  /**
   * Change le mot de passe
   */
  changePassword(
    currentPassword: string,
    newPassword: string,
  ): Observable<ApiResponse<void>> {
    if (environment.mockApi) {
      if (currentPassword === 'password123') {
        return of({ success: true, message: 'Mot de passe modifié' });
      }
      return of({ success: false, error: 'Mot de passe actuel incorrect' });
    }

    return this.http.post<ApiResponse<void>>(
      `${this.apiUrl}/security/change-password`,
      {
        currentPassword,
        newPassword,
      },
    );
  }

  /**
   * Active le 2FA - retourne le QR code
   */
  enableTwoFactor(): Observable<
    ApiResponse<{ secret: string; qrCode: string }>
  > {
    if (environment.mockApi) {
      return of({
        success: true,
        data: {
          secret: 'MOCK2FASECRETKEY',
          qrCode: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUg...',
        },
      });
    }

    return this.http.post<ApiResponse<{ secret: string; qrCode: string }>>(
      `${this.apiUrl}/security/2fa/enable`,
      {},
    );
  }

  /**
   * Vérifie le code 2FA et l'active définitivement
   */
  verifyTwoFactor(
    token: string,
  ): Observable<ApiResponse<{ backupCodes: string[] }>> {
    if (environment.mockApi) {
      if (token === '123456') {
        return of({
          success: true,
          data: {
            backupCodes: [
              'ABC123',
              'DEF456',
              'GHI789',
              'JKL012',
              'MNO345',
              'PQR678',
              'STU901',
              'VWX234',
            ],
          },
        });
      }
      return of({ success: false, error: 'Code invalide' });
    }

    return this.http.post<ApiResponse<{ backupCodes: string[] }>>(
      `${this.apiUrl}/security/2fa/verify`,
      { token },
    );
  }

  /**
   * Désactive le 2FA (mot de passe et code de l'application ou code de secours)
   */
  disableTwoFactor(
    password: string,
    code: string,
  ): Observable<ApiResponse<void>> {
    if (environment.mockApi) {
      return of({ success: true, message: '2FA désactivé' });
    }

    return this.http.post<ApiResponse<void>>(
      `${this.apiUrl}/security/2fa/disable`,
      {
        password,
        code,
      },
    );
  }

  /**
   * Exporte les données utilisateur (RGPD)
   */
  exportUserData(): Observable<Blob> {
    if (environment.mockApi) {
      const mockData = {
        user: { email: 'test@example.com', businessName: 'Ma Boulangerie' },
        products: [],
        sales: [],
      };
      return of(
        new Blob([JSON.stringify(mockData, null, 2)], {
          type: 'application/json',
        }),
      );
    }

    return this.http.get(`${this.apiUrl}/data/export`, {
      responseType: 'blob',
    });
  }

  /**
   * Avant suppression : abonnement résilié en fin de période, code 2FA exigé (BUG-04)
   */
  getAccountDeletionPreview(): Observable<ApiResponse<AccountDeletionPreview>> {
    if (environment.mockApi) {
      return of({
        success: true,
        data: {
          paid: false,
          plan: 'free',
          periodEnd: null,
          twoFactorRequired: false,
        },
      });
    }

    return this.http.get<ApiResponse<AccountDeletionPreview>>(
      `${this.apiUrl}/account/deletion`,
    );
  }

  /**
   * Supprime le compte et toutes ses données (mot de passe, code 2FA si activée, case cochée)
   */
  deleteAccount(
    password: string,
    code: string,
  ): Observable<ApiResponse<{ periodEnd: string | null }>> {
    if (environment.mockApi) {
      return of({
        success: true,
        message: 'Compte supprimé',
        data: { periodEnd: null },
      });
    }

    return this.http.delete<ApiResponse<{ periodEnd: string | null }>>(
      this.apiUrl + '/account',
      { body: { password, code: code || undefined, confirmed: true } },
    );
  }

  // ========================================================================
  // Mock Data
  // ========================================================================

  private getMockSettings(): ApiResponse<UserSettings> {
    const savedSettings = sessionStorage.getItem('userSettings');
    const defaultSettings: UserSettings = {
      emailNotifications: true,
      pushNotifications: false,
      weeklyReport: true,
      conservatismLevel: 'medium',
      autoAdjust: true,
      defaultExportFormat: 'pdf',
      includeGraphs: true,
      language: 'fr',
      theme: 'light',
      twoFactorEnabled: false,
      notificationPreferences: {
        email: {
          enabled: true,
          stockAlerts: true,
          recommendations: true,
          events: true,
          promotions: false,
        },
        push: {
          enabled: false,
          urgentAlerts: true,
          dailyReminders: false,
        },
        weeklyReport: {
          enabled: true,
          dayOfWeek: 1,
          hourOfDay: 8,
        },
      },
      algorithmWeights: {
        weather: 1.0,
        events: 1.0,
        history: 1.0,
      },
      dashboardPreferences: {
        defaultView: 'week',
        showWeather: true,
        showEvents: true,
        showTips: true,
        pinnedWidgets: [],
      },
    };

    const settings = savedSettings
      ? { ...defaultSettings, ...JSON.parse(savedSettings) }
      : defaultSettings;
    this._settings.set(settings);

    return {
      success: true,
      data: settings,
    };
  }

  private mockUpdateSettings(
    updates: Partial<UserSettings>,
  ): ApiResponse<UserSettings> {
    const current = this._settings() || this.getMockSettings().data!;
    const updated = { ...current, ...updates };

    sessionStorage.setItem('userSettings', JSON.stringify(updated));
    this._settings.set(updated);

    return {
      success: true,
      message: 'Paramètres mis à jour avec succès',
      data: updated,
    };
  }

  // ========================================================================
  // Helpers
  // ========================================================================

  private arrayBufferToBase64(buffer: ArrayBuffer | null): string {
    if (!buffer) return '';
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}
